import { Provider } from '@unipile/types';
import { useHostedAuthSession } from './useHostedAuthSession';

export function shouldUseUnipileProxy(provider: Provider): boolean {
  return provider === 'linkedin' || provider === 'whatsapp' || provider === 'instagram' || provider === 'facebook';
}

/**
 * @description This hook is used to determine if the location config should be shown for a given provider. Already used inside the LocationConfig component.
 * @param provider - The provider to check.
 * @returns An object containing the following properties:
 * - allowUserProxyOverride: Whether the user is allowed to override the proxy.
 * - allowUserCountryOverride: Whether the user is allowed to override the country.
 * - providerWithAutoProxy: Whether the provider uses Unipile's proxy.
 * - showLocationConfig: Whether the location config should be shown.
 */
export function useShowLocationConfig(provider: Provider) {
  const { data: session } = useHostedAuthSession();
  const providerWithAutoProxy = shouldUseUnipileProxy(provider);

  const allowUserProxyOverride = Boolean(session?.config?.global?.allow_user_proxy_override);

  // allow_user_country_override is effective only if there is an auto proxy for the provider
  const allowUserCountryOverride = Boolean(providerWithAutoProxy && session?.config?.global?.allow_user_country_override);

  return {
    allowUserProxyOverride,
    allowUserCountryOverride,
    providerWithAutoProxy,
    showLocationConfig: allowUserProxyOverride || allowUserCountryOverride || false,
  };
}
